import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { memo, useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import NavBarButton from '../NavBarButton/NavBarButton'
import ContactContext from 'contexts/contactContext'
import useToggle from 'hooks/useToggle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import NavBarContactsItems from './NavBarContactsItems'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { matchPath, useLocation } from 'react-router'
import useIsOfferPage from 'hooks/Navigation/useIsOfferPage'
import ModalContext from 'contexts/ModalContext'
import Modal from 'LuxKit/Modal/Modal'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import GeoContext from 'contexts/geoContext'
import useOrders from 'hooks/Order/useOrders'
import { useAppSelector } from 'hooks/reduxHooks'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import { FLIGHT_DEAL_PAGE_PATH } from 'constants/flight'

interface Props {
  className?: string
  /**
   * The minimum breakpoint when the text disappears
   */
  textBreakpoint?: App.ScreenSize
}

function NavBarContactsButton(props: Props) {
  const { className, textBreakpoint } = props
  const [open, toggleOpen, _, closeDropdown] = useToggle()
  const intl = useIntl()

  const { currentRegionCode } = useContext(GeoContext)
  const chatEnabled = useAppSelector(isSupportAssistantEnabled)
  const [upcomingOrders] = useOrders('upcoming')
  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const contact = useContext(ContactContext)
  const { pathname } = useLocation()
  const isOfferPage = useIsOfferPage()
  const isFlightDealPage = !!matchPath(pathname, { path: FLIGHT_DEAL_PAGE_PATH })

  const isOnUnrelatedOfferPage = useMemo(() => {
    if (isFlightDealPage) return true
    if (!isOfferPage) return false

    const upcomingOfferIds = upcomingOrders.flatMap(order => [
      ...order.items.map(item => item.offerId),
      ...order.customOfferItems.map(item => item.custom_item.offerId),
      ...order.bedbankItems.map(item => item.offer?.id).filter(Boolean),
      ...order.cruiseItems.map(item => item.cruiseOfferId),
      ...order.tourItems.map(item => item.tourId),
      ...order.experienceItems.map(item => item.experienceId),
    ])

    return !upcomingOfferIds.some(offerId => pathname.includes(offerId))
  }, [isOfferPage, isFlightDealPage, upcomingOrders, pathname])

  const modalTitle = intl.formatMessage({
    id: 'IZKRpau05c8kn1CB-B2S',
    defaultMessage: 'Contact us 24/7',
    description: 'Modal title for the contact us dialog showing 24/7 customer support options',
  })

  const onNavClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    if (isMobile) {
      showModal(resolve => <Modal title={modalTitle}>
        <NavBarContactsItems onItemClick={resolve} />
      </Modal>)
    } else {
      toggleOpen()
    }
  }, [isMobile, showModal, toggleOpen, modalTitle])

  if (upcomingOrders.length && chatEnabled && !isOnUnrelatedOfferPage) {
    // Push the user to use our chat if they have an upcoming order instead of calling us
    return null
  }
  const has247Support = !NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)

  const contactUsText = intl.formatMessage({
    id: '40bVsRMvxubQsEdLc7Cw',
    defaultMessage: 'Contact us',
    description: 'Contact us button text for tablet and below in the support area',
  })

  const contactUs7DaysText = intl.formatMessage({
    id: 'cxbG_3T8fKEOWogBqHBr',
    defaultMessage: 'Contact us 7 days',
    description: 'Contact us text for regions without 24/7 support in the navigation bar',
  })

  const contactUs247Text = intl.formatMessage({
    id: 'XI_Bl_OMrBPp__5HkWS1',
    defaultMessage: 'Contact us 24 hours, 7 days',
    description: 'Contact us text for regions with 24/7 support in the navigation bar',
  })

  const callText = intl.formatMessage({
    id: 'iULY0d7xVnpG7idTXuMx',
    defaultMessage: 'Call',
    description: 'Call button text for mobile and tablet in the navigation bar',
  })

  return <>
    <Group direction="horizontal" gap={4} verticalAlign="center" className={className}>
      <CSSBreakpoint min={textBreakpoint}>
        <CSSBreakpoint max="tablet">
          <BodyText variant="medium" wrap="no-wrap">{contactUsText}</BodyText>
        </CSSBreakpoint>
        <CSSBreakpoint min="desktop">
          <Caption variant="medium" wrap="no-wrap">
            {!has247Support && contactUs7DaysText}
            {has247Support && contactUs247Text}
          </Caption>
        </CSSBreakpoint>
      </CSSBreakpoint>
      <NavBarButton
        size="small"
        icon={<LinePhoneIcon />}
        dropdown
        ref={buttonRef}
        onClick={onNavClick}
      >
        <CSSBreakpoint as="span" max="tablet">{callText}</CSSBreakpoint>
        <CSSBreakpoint as="span" min="desktop">{contact.defaultContact.humanReadable}</CSSBreakpoint>
      </NavBarButton>
    </Group>
    <DropdownList
      size="S"
      open={!isMobile && open}
      onClose={closeDropdown}
      anchorRef={buttonRef}
      triggerRef={buttonRef}
    >
      <NavBarContactsItems />
    </DropdownList>
  </>
}

export default memo(NavBarContactsButton)
